import { useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getFilterDropdown } from 'components/data/helpers/filters';
import { DataTableContext } from 'components/DataTable';
import { type CustomFilterDropdownProps } from 'horizon/types/TableColumnDef';
import { useGetAvailableSelectionsQuery } from 'types/atlas-graphql';

import {
  type DamageSchemaFilterColumn,
  getDamageSchemaFilterState,
  getSelectedDamageSchemaFilterValues,
} from './utils';

type Props = CustomFilterDropdownProps & {
  column: DamageSchemaFilterColumn & {
    key: string;
  };
  schemaColumns: DamageSchemaFilterColumn[];
  schemaId?: string;
};

export const ConditionalDamageSchemaFilterDropdown = ({
  column,
  schemaColumns,
  schemaId,
  selectedKeys,
  ...filterDropdownProps
}: Props) => {
  const { t } = useTranslation();
  const { filteredInfo } = useContext(DataTableContext);
  const { hasMultipleSelections, selections } = useMemo(
    () =>
      getDamageSchemaFilterState({
        columns: schemaColumns,
        currentColumnKey: column.key,
        currentSelectedKeys: selectedKeys,
        filteredInfo,
      }),
    [column.key, filteredInfo, schemaColumns, selectedKeys]
  );
  const selectedValues = useMemo(
    () => getSelectedDamageSchemaFilterValues(selectedKeys),
    [selectedKeys]
  );

  const { data, error, loading } = useGetAvailableSelectionsQuery({
    variables: {
      ...(schemaId ? { schemaId } : {}),
      selections,
    },
    skip: hasMultipleSelections,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const conditionalValues = useMemo(() => {
    if (hasMultipleSelections) {
      return selectedValues;
    }
    if (error || loading) {
      return selectedValues.length ? selectedValues : undefined;
    }

    const availableSelections = data?.getAvailableDamageSchemaAttributeSelections ?? [];
    const currentSelection = availableSelections.find(
      selection =>
        selection.attr.name === column.key &&
        selection.__typename === 'DamageSchemaAttributeSelectionString'
    );

    const schemaValues =
      currentSelection?.__typename === 'DamageSchemaAttributeSelectionString'
        ? currentSelection.options
        : [];

    return Array.from(new Set([...selectedValues, ...schemaValues]));
  }, [column.key, data, error, hasMultipleSelections, loading, selectedValues]);

  // Keep suggestions in a ref so the select-filter component type stays stable across
  // selection updates (avoids remount + scroll reset when options move into Suggested).
  const conditionalValuesRef = useRef(conditionalValues);
  conditionalValuesRef.current = conditionalValues;

  const SelectFilterDropdown = useMemo(
    () =>
      getFilterDropdown({
        ...column.config,
        type: 'string',
        conditionalValuesRef,
        conditionalEmptyText: t(
          'table.noSchemaCombinationsAvailable',
          'No schema combinations available'
        ),
        conditionalLabel: t('table.suggested', 'Suggested'),
        remainingLabel: t('table.everythingElse', 'Everything Else'),
      }),
    [column.config, t]
  );

  return <SelectFilterDropdown {...filterDropdownProps} selectedKeys={selectedKeys} />;
};
